import { useContext } from 'react';
import { useGetIarAgencyMembersQuery } from '@rsos/reduxDataStore/src/features/eventShare/eventShareApi';
import type {
  IarAgency,
  IarMember,
} from '@rsos/reduxDataStore/src/features/eventShare/types';
import type { IarSelectedMember } from './helpers';
import { IarMockMembersContext } from './iarMembersContext';

/** Case-insensitive search across name and position fields. */
export const matchesSearch = (member: IarMember, term: string): boolean => {
  const lower = term.toLowerCase();
  return (
    member.firstName.toLowerCase().includes(lower) ||
    member.lastName.toLowerCase().includes(lower) ||
    `${member.lastName}, ${member.firstName}`.toLowerCase().includes(lower) ||
    (member.position || '').toLowerCase().includes(lower)
  );
};

/** Builds the IarSelectedMember payload sent on the create-session POST. */
export const buildSelection = (
  member: IarMember,
  agency: IarAgency,
): IarSelectedMember => ({
  agency_id: agency.legacyId,
  member_id: member.legacyId,
  name: `${member.firstName} ${member.lastName}`,
  subscriber_name: agency.name,
});

/**
 * Resolves the member list for an agency. If a mock map is supplied via
 * IarMockMembersContext (stories/tests), returns that synchronously. Otherwise
 * delegates to the live RTK Query call. The query is always invoked to obey
 * the rules of hooks but is skipped when mock data is present.
 */
export const useResolvedMembers = (org: string, agencyId: number) => {
  const mockMap = useContext(IarMockMembersContext);
  const mockMembers = mockMap?.[agencyId];
  const queryResult = useGetIarAgencyMembersQuery(
    { org, agencyId },
    { skip: mockMembers != null },
  );
  if (mockMembers) {
    return { data: mockMembers, isLoading: false, isError: false };
  }
  return queryResult;
};

/** Returns true if any agency member matches the search term. */
export const useAgencyHasMatches = (
  org: string,
  agencyId: number,
  searchTerm: string,
): boolean => {
  const { data: members } = useResolvedMembers(org, agencyId);
  if (!searchTerm) return true;
  return (members ?? []).some(m => matchesSearch(m, searchTerm));
};
